import { FC, useReducer, useContext, useState, useEffect } from "react";
import {
  HANDLE_WALLET,
  OPEN_BUY_GAINS_MODEL,
  OPEN_GAINS_CONFIRMATION_MODEL,
  UPDATE_WALLET_BALANCE,
  SET_USER_WALLETS,
  ADD_NEW_WALLET,
  WALLET_NOT_FOUND,
} from "../action";
import WalletContext from "./WalletContext";
import AlertContext from "../Alert/AlertContext";
import WalletReducer from "./WalletReducer";
import {
  ChainName,
  GetUserWallets,
  AddWallet,
  LockToken,
  ContributeToPool,
  ClaimToken,
  RelockGains,
  WithdrawGains,
  NetworkId,
  getTotalClaimed,
  StakeTokenFunction,
} from "./WalletFunction";
import {
  CancelSubscription,
  RelockSubscription,
  VerifyLockTokenTransaction,
  VerifyUnlockTokenTransaction,
} from "../Subscription/SubscriptionFunction";
import {
  getAllowance,
  getTokenBalance,
} from "../../utils/web3/callHelpers";
import { Contracts } from "../../config/contracts";
import { getBalanceAmount, getBalanceNumber } from "../../utils/web3/formatBalance";
import AuthContext from "../Auth/AuthContext";
import { useAccount, useConfig, useConnect, useDisconnect } from "wagmi";
import web3Helper, { LowGasError } from "../../services/web3";
import { UserRejectedRequestError, WaitForTransactionReceiptTimeoutError } from "viem";
import { getSupportedChainIds } from "../../services/web3/constants/chains";

const supportedChainIds: number[] = getSupportedChainIds();

const initialState = {
  address: null,
  walletId: null,
  amount: 0,
  isconnected: false,
  chainId: null,
  currency: null,
  gainsBalance: 0,
  networkId: null,
  wallets: [],
  provider: null,
  connectorByName: null,
  balancePollId: null,
  hasKycWallet: false,
};

const AppWallet: FC = (props) => {
  const { setAlert } = useContext<any>(AlertContext);
  const { isAuth } = useContext<any>(AuthContext);
  const [state, dispatch] = useReducer(WalletReducer, initialState);
  const [getBalance, setgetBalance] = useState<any>();

  const { chain } = useAccount();
  const config = useConfig();
  const { disconnect } = useDisconnect({ config });
  
  useEffect(() => {
    if (!isAuth) {
      dispatch({
        type: SET_USER_WALLETS,
        payload: [],
      });
      return;
    } else {
      getUserWallets();
    }
  }, [isAuth]);

  const getUserWallets = () => {
    GetUserWallets()
      .then((wallets: any) => {
        dispatch({
          type: SET_USER_WALLETS,
          payload: wallets,
        });
      })
      .catch((error: any) => {
        console.error(error);
      });
  };

  const handleAccountChange = async (account: `0x${string}`, chain: any) => {
    if (account && chain) {
      if (!supportedChainIds.includes(chain.id)) {
        setAlert("Selected network not supported on the platform.", "error");
        return;
      }
      walletManager(chain.id, account);
    }
  }

  const addressManager = async (address: any) => {
    let { wallets } = state;
    if (!wallets || wallets.length === 0) {
      wallets = await GetUserWallets();
    }
    var exist = await wallets.filter(
      (wallet: any) => wallet.address.toLowerCase() === address.toLowerCase()
    );

    if (exist.length === 0) {
      return false;
    }
    return exist[0];
  };

  const walletManager = async (
    chainId: number,
    account: `0x${string}`,
  ) => {
    const wallet = await addressManager(account);

    if (wallet) {
      const { chainName } =
        await fetchAccountData(chainId, account);

      dispatch({
        type: HANDLE_WALLET,
        payload: {
          address: wallet.address,
          walletId: wallet.id,
          chainName: chainName,
          networkId: NetworkId(chainId)
        },
      });
    } else {
      addNewAddress(account)
        .then(async (wallet: any) => {
          const { chainName } = await fetchAccountData(chainId, wallet.address);
          dispatch({
            type: ADD_NEW_WALLET,
            payload: wallet,
          });
          dispatch({
            type: HANDLE_WALLET,
            payload: {
              address: wallet.address,
              walletId: wallet.id,
              chainName: chainName,
              networkId: NetworkId(chainId),
            },
          });
        })
        .catch((err) => {
          if (err.code && err.code === 4001) {
            setAlert(err.message, "error");
          } else {
            setAlert("This wallet address is registered with another user account, please choose a different wallet address.", "error");
          }
          dispatch({
            type: WALLET_NOT_FOUND,
          });
          disconnect();
        });
    }
  };

  const fetchAccountData = async (
    chainId: number,
    account: `0x${string}`,
  ) => {
    console.info("Fetching account data: " + chainId + " " + account)
    const chainName = ChainName(chainId);
    return { chainName }
  };

  async function addNewAddress(address: any) {
    return new Promise((resolve, reject) => {
      const message: string =
`Welcome to GainsPad!

Click to sign in and accept the Gains Associates Terms of Service (https://www.gains-associates.com/terms&conditions) and Privacy Policy (https://www.gains-associates.com/privacy-policy).

This is a secure login request, no blockchain transaction will be made and no gas fees will be incurred.`
      web3Helper.signMessage(message)
      .then((signature: `0x${string}` | null) => {
        if (signature) {
          AddWallet(message, address, signature)
          .then((walletData: any) => {
            setAlert("Wallet verified and added", "success");
            dispatch({
              type: ADD_NEW_WALLET,
              payload: walletData,
            });
            resolve(walletData);
          })
          .catch((error: any) => {
            reject(error)
          })
        }
      })
      .catch((error) => {
        disconnect();
        if (error instanceof UserRejectedRequestError) setAlert("User rejected the signature request. Please try again.", "error");
        else setAlert("Unable to complete signature request. Please try again", "error");
      })
    })
  }

  // Gas fees calculator
  // const hasEnoughGas = () => {
  //   return new Promise(async (resolve, reject) => {
  //     const formattedFeeData = await web3Helper.getGasFees()
  //     // Todo: Fetch account balance for given chain id
  //     // Compare and return accordingly
  //     // TODO. needs to calculate with the following formula. gasFee = gasPrice * gasLimit
  //     console.info(formattedFeeData);
  //     resolve(true);
  //   });
  // };

  // open respective Gains modal function on the basis of gain balance
  const gainsTokenModal = async (tickets?: any) => {
    if (getBalance < 1000) {
      console.info("Tickets " + tickets);
      if (!tickets || (tickets && tickets < 1)) {
        dispatch({
          type: OPEN_BUY_GAINS_MODEL,
        });
      } else {
        dispatch({
          type: OPEN_GAINS_CONFIRMATION_MODEL,
        });
      }
    } else {
      dispatch({
        type: OPEN_GAINS_CONFIRMATION_MODEL,
      });
    }
  };

  const lockToken = (
    gainsAmount: any,
    duration: any,
    subid: any,
    tokenAddress: any
  ) => {
    return new Promise((resolve, reject) => {
      LockToken(
        chain?.id,
        gainsAmount,
        duration,
        subid,
        tokenAddress
      )
        .then(async (transactionHash: any) => {
          // TODO: Add a watchBalance feature so that this is 
          // not needed.
          resolve(transactionHash);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const stakeToken = (
    tokenAmount: number,
    contractAddress: `0x${string}`,
    chainId: number,
    tokenAddress: `0x${string}`
  ) => {
    return new Promise((resolve, reject) => {
      StakeTokenFunction(
        tokenAmount,
        contractAddress,
        chainId,
        tokenAddress,
      )
      .then(async (transactionHash: any) => {
        resolve(transactionHash)
      })
      .catch((error: any) => {
        reject(error);
      })
    })
  }

  const verifyLockToken = (subid: any, transactionHash: any, address: any) => {
    return new Promise((resolve, reject) => {
      VerifyLockTokenTransaction(subid, transactionHash, address)
        .then(async (data: any) => {
          resolve(data.verifyLockTokenTransaction);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const approveTokenSpent = (
    chainId: number,
    tokenAmount: any,
    tokenAddress: any,
    spender: any
  ) => {
    return new Promise((resolve, reject) => {
      web3Helper.approveTokenSpend(
        chainId,
        tokenAddress,
        tokenAmount,
        spender,
      )
      .then((receipt: any) => {
        resolve(receipt.transactionHash);
      })
      .catch((error: any) => {
        reject(error);
      });
    });
  };

  const fetchTokenAllowance = async (
    chainId: number,
    tokenAddress: `0x${string}`,
    spender: `0x${string}`,
    owner: `0x${string}`
  ) => {
    let allowance = await getAllowance(chainId, owner, spender, tokenAddress);
    return allowance;
  };

  const fetchTokenClaimed = async (
    chainId: number,
    claimContractAddress: string,
    address: string,
  ) => {
    let { tokenClaimed, decimals } = await getTotalClaimed(chainId, claimContractAddress, address);
    const tokenClaimedInNum = getBalanceNumber(tokenClaimed, decimals);
    return tokenClaimedInNum;
  }

  const fetchContributed = async (
    networks: { address: `0x${string}`; networkId: number }[],
    account: `0x${string}`
  ) => {
    const res: Record<string, number> = {};
    for (const network of networks) {
      const pledgeContributorInstance = web3Helper.getPledgeContract(
        network.address,
        network.networkId
      );
      const contributed: any = await pledgeContributorInstance.read.contributed(
        [account]
      );
      const contributedInNum = getBalanceAmount(
        contributed.toString(), 18).toNumber()

      res[`${network.address}-${network.networkId}`] = contributedInNum;
    }

    return res;
  };

  const getContributionDetails = (
    chainId: number,
    address: `0x${string}`,
    token: `0x${string}`
  ) => {
    return new Promise( async (resolve, reject) => {
      try {
        web3Helper.getPledgeContract(address, chainId).read.contribution()
        .then((result: any) => {
          const cap = getBalanceAmount(result[0].toString(), 18).toNumber();
          const amount = getBalanceAmount(result[1].toString(), 18).toNumber();
          const contribution = {
            cap: cap,
            amount: amount,
          }
          resolve(contribution);
        })
        .catch((error: Error) => {
          reject(error);
        })
      } catch (error) {
        reject(error);
      }
    });
  }

  const contribute = (
    chainId: number,
    amount: any,
    proof: any,
    contractAddress: any,
    tokenAddress: any,
    maxValue: any,
    source: any
  ) => {
    return new Promise((resolve, reject) => {
      ContributeToPool(
        source,
        amount,
        proof,
        contractAddress,
        tokenAddress,
        maxValue,
        chainId
      )
        .then((transactionHash: any) => {
          resolve(transactionHash);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const relockGains = (
    chainId: number,
    account: string,
    existingSubscriptionId: any,
    scVersion: number,
  ) => {
    return new Promise((resolve, reject) => {
      // Call relock mutatation
      RelockSubscription(existingSubscriptionId)
        .then((data: any) => {
          setAlert("Initiating subscription relock...", "info");
          RelockGains(
            account,
            existingSubscriptionId,
            data.relockToken.id,
            data.relockToken.duration,
            scVersion
          )
            .then((transactionHash: any) => {
              // Verify relock using lock transaction
              setAlert("Tokens successfully locked on chain.", "success");
              VerifyLockTokenTransaction(
                data.relockToken.id,
                transactionHash,
                account
              )
                .then((data: any) => {
                  setAlert("Successfully relocked tokens.", "successs");
                  // TODO - check if tickets have to be updated.
                  resolve(data);
                })
                .catch((error: any) => {
                  setAlert(
                    "Tokens locked on chain, verification pending.",
                    "info"
                  );
                  reject(error);
                });
            })
            .catch((error: any) => {
              // We call cancel subscription as blockchain transaction failed
              if (error instanceof LowGasError) {
                setAlert("Insufficient balance to pay for gas. Please top up your account and try again.", "error");
                CancelSubscription(data.relockToken.id, 'UserRejectedRequestError')
                  .then((data: any) => {
                    dispatch({
                      type: "CANCEL_PENDING_SUBSCRIPTION",
                      payload: data.relockToken.id,
                    });
                  })
                  .catch((error: any) => {
                    console.log(error);
                  });
              } else {
                const isUserRejectedRequestError = error.walk((e: any) => e instanceof UserRejectedRequestError) instanceof UserRejectedRequestError
                if (isUserRejectedRequestError) {
                  setAlert(`Please approve the transaction on your wallet to re-stake GAINS.`, "error");
                  CancelSubscription(data.relockToken.id, 'UserRejectedRequestError')
                    .then((data: any) => {
                      dispatch({
                        type: "CANCEL_PENDING_SUBSCRIPTION",
                        payload: data.relockToken.id,
                      });
                    })
                    .catch((error: any) => {
                      console.log(error);
                    });
                } else {
                  setAlert(
                    `We ran into an issue receiving transaction verification. Please allow us some time to check for confirmation.`,
                    "warning",
                    10000
                  );
                }
              }
              reject(error);
            });
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const withdrawGains = (
    chainId: number,
    address: `0x${string}`,
    subscriptionId: any,
    scVersion: number,
  ) => {
    return new Promise((resolve, reject) => {
      WithdrawGains(address, subscriptionId, scVersion)
        .then((transactionHash: any) => {
          setAlert("Successfully withdrawn GAINS on chain.", "success");
          VerifyUnlockTokenTransaction(
            subscriptionId,
            transactionHash,
            address
          )
            .then(async (data) => {
              setAlert("Token withdrawl successfully captured.", "success");
              resolve(data);
              const gainsBalance = await getTokenBalance(
                chainId,
                address,
                Contracts.gains[chainId] as `0x${string}`
              );
              dispatch({
                type: UPDATE_WALLET_BALANCE,
                payload: gainsBalance,
              });
            })
            .catch((error: any) => {
              setAlert(
                "Failed to capture withdrawal, will be updated shortly.",
                "warning"
              );
              reject(error);
            });
        })
        .catch((error: any) => {
          if (error instanceof LowGasError) {
            setAlert("Not enough gas to pay for transaction, please topup to continue withdrawal", "error")
          } else {
            setAlert(
              "Failed to execute withdrawal, please try again later.",
              "error"
            );
          }
          reject(error);
        });
    });
  };

  const claimToken = (
    chainId: number,
    proof: any,
    tokenAmount: any,
    tokenAddress: any,
    claimTokenContractAddress: any,
    source: any
  ) => {
    return new Promise((resolve, reject) => {
      // Wallet should be forced to used network with `networkId` and wallet with
      // address `source`.
      ClaimToken(
        chainId,
        proof,
        tokenAmount,
        tokenAddress,
        claimTokenContractAddress,
        source,
      )
        .then((transactionHash: any) => {
          setAlert("Token successfully claimed.", "success");
          resolve(transactionHash);
        })
        .catch((error: any) => {
          setAlert("Failed to claim tokens.", "error");
          reject(error);
        });
    });
  };

  return (
    <WalletContext.Provider
      value={{
        address: state.account,
        walletId: state.walletId,
        amount: state.amount,
        currency: state.currency,
        openmodal: state.openmodal,
        gainsBalance: state.gainsBalance,
        chainId: state.chainId,
        networkId: state.networkId,
        provider: state.provder,
        connectorByName: state.connectorByName,
        wallets: state.wallets,
        isConnected: state.isconnected,
        hasKycWallet: state.hasKycWallet,
        handleAccountChange,
        getContributionDetails,
        walletManager,
        getUserWallets,
        gainsTokenModal,
        lockToken,
        stakeToken,
        verifyLockToken,
        fetchTokenAllowance,
        fetchContributed,
        approveTokenSpent,
        contribute,
        relockGains,
        withdrawGains,
        claimToken,
        fetchTokenClaimed,
      }}
    >
      {props.children}
    </WalletContext.Provider>
  );
};

export default AppWallet;

